import { FacetGroup, ProductFilterChange } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import {
  CategorySorting,
  DropdownField,
  DropdownOption,
  INITIAL_CATEGORY_STATE,
} from '@components';
import { useAppSelector } from '@hooks';
import { useCategory } from '@hooks/category/category.hook';
import { Clear } from '@mui/icons-material';
import { Box, Button, Grid, styled } from '@mui/material';
import { breakpoints } from '@themes/theme.helpers';
import { t } from '@translations';
import { extractFilterValue } from '@utils/products.utils';

// STYLES
//--------
const BoxWrapper = styled(Box)`
  margin-top: 20px;
  margin-bottom: 20px;
  display: flex;
  gap: 32px;
  flex-direction: column;
  ${breakpoints.up('md')} {
    margin-bottom: 16px;
    gap: 16px;
    flex-direction: row;
    align-items: top;
    align-content: center;
    margin-top: 0;
  }
`;

const SortingBox = styled(Box)`
  margin-bottom: 12px;
  ${breakpoints.up('md')} {
    margin-left: auto;
  }
`;

// TYPES
//-------

export type CategoryFiltersProps = {
  isWorking?: boolean;
};

// COMPONENTS
//------------

/**
 * Display filters
 */
export const CategoryFilters = ({ isWorking }: CategoryFiltersProps) => {
  const showClearButton = hasActiveFilters();

  const { handleFilterChange, handleSortingChange, handleClearFilters } = useCategory();

  const allFilters = useAppSelector((state) => state.catalog.filters)?.filter((filterItem) => {
    const hasAtLeastOneOption = filterItem?.facets?.filter((x) => x.name && x.key).length;
    return (filterItem?.groupFieldName || filterItem?.groupName) && hasAtLeastOneOption;
  });

  const handleChange = ({ fieldName = '', fieldValue = '', fieldLabel = '' }) => {
    if (typeof handleFilterChange === 'function') {
      handleFilterChange({ fieldName, fieldValue, fieldLabel });
    }
  };

  return (
    <BoxWrapper>
      <Grid
        container
        spacing={2}
        sx={{
          flexWrap: {
            xs: 'wrap-reverse',
            lg: 'wrap',
          },
        }}
      >
        <Grid
          item
          xs={allFilters?.length === 1 ? 6 : 12}
          md={allFilters?.length === 1 ? 6 : 12}
          lg={9}
          display={{
            xs: 'none',
            md: 'block',
          }}
        >
          <Grid container spacing={2}>
            {allFilters?.map((filter, key) => (
              <Grid
                item
                xs={12}
                md={allFilters?.length === 1 ? 12 : 6}
                lg={4}
                key={filter.groupFieldName + '-' + key}
              >
                <CategoryFilter filter={filter} onChange={handleChange} disabled={isWorking} />
              </Grid>
            ))}
            {showClearButton ? (
              <Grid item>
                <Button
                  variant='tertiary'
                  onClick={handleClearFilters}
                  startIcon={<Clear />}
                  disabled={isWorking}
                >
                  {t(TextCodes.Base.Clear)}
                </Button>
              </Grid>
            ) : null}
          </Grid>
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          lg={3}
          sx={{
            marginInlineStart: {
              xs: 'auto',
              lg: '0',
            },
          }}
        >
          <SortingBox>
            <CategorySorting
              onChange={handleSortingChange}
              isWorking={isWorking}
              productCount={INITIAL_CATEGORY_STATE.itemPerPage}
            />
          </SortingBox>
        </Grid>
      </Grid>
    </BoxWrapper>
  );
};

export const FilterBox = styled(Box)`
  min-width: 200px;
  display: inline-block;
  width: 100%;
  margin-bottom: 12px;
`;

export type CategoryFilterProps = {
  filter: FacetGroup;
  onChange?: ({ fieldName, fieldValue, fieldLabel }: ProductFilterChange) => void;
  dropdownZIndex?: number;
  disabled?: boolean;
  value?: string;
};

export const CategoryFilter = ({
  filter,
  onChange,
  dropdownZIndex,
  disabled,
  value = '',
}: CategoryFilterProps) => {
  const { groupName = '', groupFieldName = '' } = filter;
  const searchParams = new URLSearchParams(window.location.search);
  const currentFilterValue = searchParams.get(groupName);

  const handleChange = ({ fieldName = '', fieldValue = '' }) => {
    if (typeof onChange === 'function') {
      onChange({ fieldName, fieldValue, fieldLabel: groupName });
    }
  };
  const options: DropdownOption<string>[] =
    filter?.facets?.map((x) => ({
      value: x.key || '',
      label: x.name || '',
    })) || [];

  const currentValue =
    options.find((option) => extractFilterValue(option.value) === currentFilterValue)?.label ||
    value;

  return (
    <FilterBox key={groupFieldName}>
      <DropdownField
        disabled={disabled}
        name={groupFieldName}
        value={currentValue}
        options={options}
        label={groupName}
        placeholder={groupName}
        dropdownZIndex={dropdownZIndex}
        getValue={(value: string | string[]) =>
          handleChange({
            fieldName: groupFieldName,
            fieldValue: value as string,
          })
        }
        withRemoveButton
        fullWidth
      />
    </FilterBox>
  );
};

export const hasActiveFilters = () => {
  const searchParams = new URLSearchParams(window.location.search);
  let active = false;
  for (let [key, value] of searchParams.entries()) {
    if (key !== 'page' && key !== 'sorting' && value.trim() !== '') {
      active = true;
      break;
    }
  }
  return active;
};
